import { useEffect, useState } from 'react'

import {
  MarketplaceField,
  ContractStatus,
  OfferStatus,
  SignatureRequestStatus,
  BookingStatus
} from '@graphql'
import { jsonata } from '@/utils/jsonata'
import { ColorTag } from '@/components/ColorTag'
import { Colors } from '@/constants/colors'

type AsyncCustomFieldProps = {
  field: MarketplaceField
  data: any
}

const ContractTag = ({ status }: { status: ContractStatus | null }) => {
  switch (status) {
    case ContractStatus.Open:
      return <ColorTag color={Colors.BLUE}>{status}</ColorTag>
    case ContractStatus.Closed:
      return <ColorTag color={Colors.RED}>{status}</ColorTag>
    case ContractStatus.Settled:
      return <ColorTag color={Colors.GREEN}>{status}</ColorTag>
    default:
      return <ColorTag>{status}</ColorTag>
  }
}

const SettlementTag = ({ status }: { status: string | null }) => {
  if (status === 'Paid')
    return <ColorTag color={Colors.GREEN}>{status}</ColorTag>
  return status ? (
    <ColorTag color={Colors.BLUE}>{status}</ColorTag>
  ) : (
    <ColorTag>{status}</ColorTag>
  )
}

const BookingTag = ({ status }: { status: BookingStatus | null }) => {
  switch (status) {
    case BookingStatus.Open:
      return <ColorTag color={Colors.BLUE}>{status}</ColorTag>
    case BookingStatus.Closed:
    default:
      return <ColorTag>{status}</ColorTag>
  }
}

const OfferTag = ({ status }: { status: OfferStatus | null }) => {
  switch (status) {
    case OfferStatus.Acknowledged:
      return <ColorTag color={Colors.BLUE}>{status}</ColorTag>
    case OfferStatus.Matched:
      return <ColorTag color={Colors.BLUE}>{status}</ColorTag>
    case OfferStatus.Rejected:
      return <ColorTag color={Colors.RED}>{status}</ColorTag>
    case OfferStatus.Accepted:
      return <ColorTag color={Colors.GREEN}>{status}</ColorTag>
    case OfferStatus.Canceled:
      return <ColorTag color={Colors.YELLOW}>{status}</ColorTag>
    case OfferStatus.Pending:
      return <ColorTag color={Colors.YELLOW}>{status}</ColorTag>
    default:
      return <ColorTag>{status}</ColorTag>
  }
}

const SignatureTag = ({
  status
}: {
  status: SignatureRequestStatus | null
}) => {
  switch (status) {
    case SignatureRequestStatus.Draft:
      return <ColorTag color={Colors.GRAY}>{status}</ColorTag>
    case SignatureRequestStatus.Pending:
      return <ColorTag color={Colors.YELLOW}>{status}</ColorTag>
    case SignatureRequestStatus.Completed:
      return <ColorTag color={Colors.GREEN}>{status}</ColorTag>
    case SignatureRequestStatus.Declined:
      return <ColorTag color={Colors.RED}>{status}</ColorTag>
    case SignatureRequestStatus.Expired:
      return <ColorTag color={Colors.RED}>{status}</ColorTag>
    default:
      return <ColorTag>{status}</ColorTag>
  }
}

const AsyncCustomField = ({ field, data }: AsyncCustomFieldProps) => {
  const [result, setResult] = useState(null)

  useEffect(() => {
    const evaluateExpression = async () => {
      try {
        const expression = jsonata(field?.jsonata ?? '')
        const result = await expression.evaluate(data)
        setResult(result)
      } catch (error) {
        setResult(null)
      }
    }

    evaluateExpression()
  }, [field?.jsonata, data])

  if (field?.styles?.includes('contractStatus')) {
    return <ContractTag status={result} />
  }

  if (field?.styles?.includes('settlementStatus')) {
    return <SettlementTag status={result} />
  }

  if (field?.styles?.includes('bookingStatus')) {
    return <BookingTag status={result} />
  }

  if (field?.styles?.includes('offerStatus')) {
    return <OfferTag status={result} />
  }

  if (field?.styles?.includes('signatureRequestStatus')) {
    return <SignatureTag status={result} />
  }

  if (field?.styles?.includes('priceChange') && result !== null) {
    const color =
      result > 0 ? 'text-green-600' : result < 0 ? 'text-red-500' : ''
    return <span className={color}>{result}</span>
  }

  return <div>{result}</div>
}

export default AsyncCustomField
